import React from 'react';
import PropTypes from 'prop-types';
import Menus from '../Svg/Menus';
import OLOff from '../Svg/OLOff';
import { EmptyStates } from '../../utils/constants';
import { navigateToAndShowBackToast, PageComponentId } from '@wix/business-manager-api';
import NoAddress from '../Svg/NoAddress';
import NoSearchResults from '../Svg/NoSearchResults';
import HeadphonesSVG from '../Svg/HeadphonesSVG';
import { translate } from 'react-i18next';

import { TextButton, Heading, Text, EmptyState } from 'wix-style-react';

const SETTINGS_PAGE_ID = PageComponentId.Settings;
const LOCATION_ROUTE = '?focus=address';

const navigateToSettingsAndShowBack = (t) =>
  navigateToAndShowBackToast(
    {
      pageComponentId: SETTINGS_PAGE_ID,
      contextData: {
        appState: LOCATION_ROUTE,
      },
    },
    {
      pageComponentId: PageComponentId.RestaurantsCallCenter,
    },
    {
      buttonText: t('bizmgr_generalsettings_addlocation_toast_phoneordering_CTA'),
      text: t('bizmgr_generalsettings_addlocation_toast'),
    },
  );

class EmptyStateHelper extends React.PureComponent {
  static propTypes = {
    type: PropTypes.oneOf(Object.values(EmptyStates)),
    msid: PropTypes.string,
    t: PropTypes.func,
  };

  render() {
    const { type } = this.props;

    const title = (
      <Heading appearance="H3" dataHook="empty-state-title">
        {this._getTitle()}
      </Heading>
    );

    const subtitle = (
      <Text size="medium" weight="normal" secondary data-hook="empty-state-subtitle">
        {this._getSubtitle()}
      </Text>
    );

    return (
      <div data-hook={`empty-state-wrapper-${type}`}>
        <EmptyState image={this._getIcon()} title={title} subtitle={subtitle}>
          {this._getLink()}
        </EmptyState>
      </div>
    );
  }

  _getIcon() {
    const { type } = this.props;

    switch (type) {
      case EmptyStates.NO_MENU:
        return <Menus />;
      case EmptyStates.OLO_OFF:
        return <OLOff />;
      case EmptyStates.NO_REST_ADDRESS:
        return <NoAddress />;
      case EmptyStates.NO_PAYMENTS:
      case EmptyStates.OLO_UNAVAILABLE:
      case EmptyStates.NO_PREMIUM:
        return <HeadphonesSVG />;
      case EmptyStates.NO_SEARCH_RESULTS:
        return <NoSearchResults />;
      default:
        return null;
    }
  }

  _getTitle() {
    const { type, additionalTitleText, t } = this.props;

    switch (type) {
      case EmptyStates.NO_MENU:
        return t('phoneordering_emptystate_addmenu_title');
      case EmptyStates.OLO_OFF:
        return t('phoneordering_emptystate_toggleoff_owner_title');
      case EmptyStates.OLO_UNAVAILABLE:
        return t('phoneordering_emptystate_outsidehours_title');
      case EmptyStates.NO_PAYMENTS:
        return t('phoneordering_emptystate_paymentmethods_title');
      case EmptyStates.NO_PREMIUM:
        return t('phoneordering_emptystate_upgrade_owner_title');
      case EmptyStates.NO_REST_ADDRESS:
        return t('phoneordering_emptystate_nolocation_title');
      case EmptyStates.NO_SEARCH_RESULTS:
        return `No results for "${additionalTitleText}"`;
      default:
        return '';
    }
  }

  _getSubtitle() {
    const { type, t } = this.props;

    switch (type) {
      case EmptyStates.NO_MENU:
        return t('phoneordering_emptystate_addmenu_body');
      case EmptyStates.OLO_OFF:
        return t('phoneordering_emptystate_toggleoff_owner_body');
      case EmptyStates.OLO_UNAVAILABLE:
        return t('phoneordering_emptystate_outsidehours_description');
      case EmptyStates.NO_PAYMENTS:
        return t('phoneordering_emptystate_paymentmethods_description');
      case EmptyStates.NO_PREMIUM:
        return t('phoneordering_emptystate_upgrade_owner_body');
      case EmptyStates.NO_REST_ADDRESS:
        return t('phoneordering_emptystate_nolocation_description');
      case EmptyStates.NO_SEARCH_RESULTS:
        return 'Try a different search.';
      default:
        return '';
    }
  }

  _getLink() {
    const { type, t } = this.props;

    switch (type) {
      case EmptyStates.NO_MENU:
        return this._getGoToSettingsButton(t('phoneordering_emptystate_addmenu_cta'));
      case EmptyStates.NO_PAYMENTS:
        return this._getGoToSettingsButton(t('phoneordering_emptystate_toggleoff_owner_cta'));
      case EmptyStates.OLO_OFF:
      case EmptyStates.OLO_UNAVAILABLE:
        return this._getGoToSettingsButton(t('phoneordering_emptystate_toggleoff_owner_cta'));
      case EmptyStates.NO_REST_ADDRESS:
        return this.getGeneralInfoLink();
      case EmptyStates.NO_PREMIUM:
        return this._getUpgradeButton();
      default:
        return null;
    }
  }

  _getGoToSettingsButton(title) {
    return (
      <TextButton onClick={() => this.props.goToSettings()} dataHook="empty-state-link">
        {title}
      </TextButton>
    );
  }

  getGeneralInfoLink() {
    const { t } = this.props;

    return (
      <TextButton onClick={() => navigateToSettingsAndShowBack(t)} dataHook="empty-state-link">
        {t('phoneordering_emptystate_nolocation_CTA')}
      </TextButton>
    );
  }

  _getUpgradeButton() {
    const { t } = this.props;

    return (
      <TextButton
        as="a"
        href={`https://premium.wix.com/wix/api/premiumStart?siteGuid=${this.props.msid}`}
        target={'_blank'}
        skin="premium"
        dataHook="empty-state-link"
      >
        {t('phoneordering_emptystate_upgrade_owner_cta')}
      </TextButton>
    );
  }
}

export default translate()(EmptyStateHelper);
